/**
 * Figma copy for globalised-folder warnings in Curve Management (WEB-1953 / WEB-2028).
 * Product language says "admin"; that maps to Hasura roles
 * `umi-internal-admin` / `umi-internal-write`.
 */

export const GLOBALISED_EDIT_SAME_FOLDER =
  "This Curve is in a globalised Folder, users from outside your organisation may be affected by any changes you make";

export const GLOBALISED_MOVE_INTO =
  "You are moving this Curve into a globalised Folder, users from outside your organisation may gain access to this Curve";

export const GLOBALISED_MOVE_BETWEEN =
  "You are moving this Curve into a new globalised Folder, users from outside your organisation may gain or lose access to this Curve";

export const GLOBALISED_MOVE_OUT =
  "You are moving this Curve out of a globalised Folder, users from outside your organisation will lose access to this Curve";

export const GLOBALISED_SAVE_CREATE =
  "This is a globalised Folder, users from outside your organisation may gain access to this Curve";

export const GLOBALISED_SAVE_MOVE_INTO =
  "This is a globalised Folder, users from outside your organisation may gain access to this Curve";

export const GLOBALISED_SAVE_MOVE_BETWEEN =
  "This is a different globalised Folder so users from outside your organisation may gain or lose access to this Curve";

export const GLOBALISED_SAVE_MOVE_OUT =
  "This Folder is not globalised, users from outside your organisation will lose access to this Curve";

export const GLOBALISED_EDIT_NB_SUFFIX =
  "This Curve is in a globalised Folder so this may include users from outside your organisation.";

/**
 * Shown to admins as a heads-up under the Curve picker / Save modal, and to
 * non-admins as the reason their edit is blocked.
 */
export const GLOBALISED_EDIT_CURVE_WARNING =
  "This Curve is in a globalised Folder, editing this Curve may affect users from outside your organisation";

export const GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP =
  "You don't have edit access to this Curve as it is shared externally.";

export const GLOBALISED_NON_MANAGER_FOLDER_TOOLTIP =
  "You don't have edit access to this Folder as it is shared externally.";

/** Inline warning under Curve Folder while editing (UMI admin). */
export function getEditFolderWarning(args: {
  sourcePackageId: string | number | null | undefined;
  targetPackageId: string | number | null | undefined;
  sourceIsGlobalised: boolean;
  targetIsGlobalised: boolean;
}): string | null {
  const { sourcePackageId, targetPackageId, sourceIsGlobalised, targetIsGlobalised } = args;
  if (!sourceIsGlobalised && !targetIsGlobalised) return null;

  const source = sourcePackageId != null ? String(sourcePackageId) : "";
  const target = targetPackageId != null ? String(targetPackageId) : "";
  const moved = !!source && !!target && source !== target;

  if (!moved && sourceIsGlobalised) return GLOBALISED_EDIT_SAME_FOLDER;
  if (!moved) return null;

  if (!sourceIsGlobalised && targetIsGlobalised) return GLOBALISED_MOVE_INTO;
  if (sourceIsGlobalised && targetIsGlobalised) return GLOBALISED_MOVE_BETWEEN;
  if (sourceIsGlobalised && !targetIsGlobalised) return GLOBALISED_MOVE_OUT;
  return null;
}

/** Warning under Select Folder in the Save modal (manager). */
export function getSaveFolderWarning(args: {
  isCreate: boolean;
  sourcePackageId: string | number | null | undefined;
  targetPackageId: string | number | null | undefined;
  sourceIsGlobalised: boolean;
  targetIsGlobalised: boolean;
}): string | null {
  const { isCreate, sourcePackageId, targetPackageId, sourceIsGlobalised, targetIsGlobalised } = args;

  if (isCreate) {
    return targetIsGlobalised ? GLOBALISED_SAVE_CREATE : null;
  }

  const source = sourcePackageId != null ? String(sourcePackageId) : "";
  const target = targetPackageId != null ? String(targetPackageId) : "";
  const moved = !!source && !!target && source !== target;

  if (!moved) {
    // Same folder save: Figma shows the edit-in-globalised message on the form,
    // not a separate save-folder line when the folder did not change.
    return null;
  }

  if (!sourceIsGlobalised && targetIsGlobalised) return GLOBALISED_SAVE_MOVE_INTO;
  if (sourceIsGlobalised && targetIsGlobalised) return GLOBALISED_SAVE_MOVE_BETWEEN;
  if (sourceIsGlobalised && !targetIsGlobalised) return GLOBALISED_SAVE_MOVE_OUT;
  return null;
}

export function isPackageGlobalised(
  packageId: string | number | null | undefined,
  globalisedIds: ReadonlySet<number>,
): boolean {
  if (packageId == null || packageId === "") return false;
  const n = typeof packageId === "number" ? packageId : Number(packageId);
  return !Number.isNaN(n) && globalisedIds.has(n);
}
